import { isApiError } from "@simpro/api-client";

export interface SalonLoadError {
  title: string;
  description: string;
  /** False when trying again cannot help: the address itself names no salon, or the wrong one. */
  canRetry: boolean;
  /** The salon is there but not taking online bookings: a refusal, not a fault (follow-up F3). */
  refusal?: boolean;
}

/**
 * What to tell a customer when the salon's page cannot load. "Check your connection" was shown for
 * every failure, including an address that names no salon, which sent people looking for a network
 * fault that was not there.
 */
export function salonLoadError(error: unknown, isDevelopment = import.meta.env.DEV): SalonLoadError {
  const code = isApiError(error) ? error.code : undefined;

  if (code === "tenant.missing") {
    return {
      title: "Which salon?",
      description: isDevelopment
        ? "This address does not name a salon. On this computer, add ?tenant= and the salon's code, for example http://localhost:5175/?tenant=acme."
        : "This address does not name a salon. Open the booking link your salon sent you.",
      canRetry: false,
    };
  }

  if (code === "tenant.unknown" || code === "module.disabled") {
    return {
      title: "We could not find this salon",
      description: "Check the link your salon sent you. It may have changed, or online booking may be switched off.",
      canRetry: false,
    };
  }

  // Suspended by the platform (unpaid, or under review): neither the connection nor a retry is the
  // answer, and the real-API journey of 2026-10-13 caught the page saying "check your connection".
  if (code === "tenant.suspended") {
    return {
      title: "This salon is not taking online bookings right now",
      description: "Please call the salon to book.",
      canRetry: false,
      refusal: true,
    };
  }

  if (code === "salon.branch_not_found") {
    return {
      title: "This salon is not taking bookings yet",
      description: "Please call the salon, or try again later.",
      canRetry: true,
    };
  }

  return {
    title: "We could not load the salon",
    description: "Check your connection and try again.",
    canRetry: true,
  };
}
